/**
 * Where the voices come out: voice chat's own AudioContext, and one small graph
 * per player being heard.
 *
 *   remote track ─► MediaStreamSource ─┬─► AnalyserNode            (speaking level)
 *                                      └─► player gain ─► voice gain ─► speakers
 *
 * ITS OWN context, not sound.ts's. That one is private to the sound manager and
 * is torn down and rebuilt after a long hide, which would cut every voice off
 * mid-sentence; and it is created lazily by game sounds, never inside the tap
 * that has to unlock voice on iOS.
 *
 * Every remote stream is ALSO handed to a muted `<audio>` element that is never
 * put in the document. Chromium does not pull remote WebRTC audio through
 * WebAudio unless a media element is consuming the stream (the spike's
 * cf-page.mjs found the same). The element stays muted so nothing is heard
 * twice: the volume a player sets has to be the gain node's, because iOS
 * ignores an `<audio>` element's volume.
 *
 * Module state rather than a class: there is one pair of speakers, and the
 * context has to exist BEFORE any voice client does - it is created in the
 * consent sheet's Continue tap, which is the only gesture an iOS first-timer
 * gives before the voices arrive.
 */
import { audioDiagEnabled, audioDiagLine, audioFixes, audioLog } from "../audio/audioDiag";
import { sound } from "../audio/sound";

interface Sink {
  stream: MediaStream;
  el: HTMLAudioElement;
  source: MediaStreamAudioSourceNode | null;
  analyser: AnalyserNode | null;
  gain: GainNode | null;
  buf: Float32Array<ArrayBuffer> | null;
}

let ctx: AudioContext | null = null;
let output: GainNode | null = null;
let outputLevel = 1;
/** An `<audio>` element's play() was refused - autoplay policy wants a tap. */
let playRefused = false;
const sinks = new Map<string, Sink>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const fn of listeners) fn();
}

/** Called whenever whether audio needs a tap may have changed. */
export function onVoiceAudioChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Creates (or revives) the voice context and asks it to run. SYNCHRONOUS on
 * purpose, and must be called from inside a tap's handler before anything is
 * awaited: iOS only lets a context start inside a user gesture, and an await
 * ends the gesture.
 */
export function ensureVoiceContext(): AudioContext | null {
  if (!ctx || ctx.state === "closed") {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
    const created = ctx;
    created.addEventListener?.("statechange", () => {
      if (created === ctx) notify();
    });
    output = ctx.createGain();
    output.gain.value = outputLevel;
    output.connect(ctx.destination);
    // A context rebuilt under live voices re-wires them, or they would play
    // into the closed one forever.
    for (const sink of sinks.values()) wire(sink);
  }
  if (ctx.state !== "running") void ctx.resume().catch(() => {});
  return ctx;
}

/** Whether a tap is needed before anything can be heard. */
export function voiceAudioNeedsUnlock(): boolean {
  // "interrupted" is iOS's own state, after a call or Siri; it needs the same tap.
  return !ctx || ctx.state !== "running" || playRefused;
}

/** Inside a tap: start the context and every element again. */
export function unlockVoiceAudio(): void {
  ensureVoiceContext();
  playRefused = false;
  for (const sink of sinks.values()) playElement(sink.el);
  notify();
}

/** The level every voice is heard at, 0..1. Ramped, so a dragged slider does not click. */
export function setVoiceOutputLevel(level: number): void {
  outputLevel = Math.max(0, Math.min(1, level));
  if (!ctx || !output) return;
  const t0 = ctx.currentTime;
  output.gain.cancelScheduledValues(t0);
  output.gain.setValueAtTime(output.gain.value, t0);
  output.gain.linearRampToValueAtTime(outputLevel, t0 + 0.06);
}

function playElement(el: HTMLAudioElement): void {
  el.play().catch((err: unknown) => {
    if (err instanceof DOMException && err.name === "NotAllowedError") {
      playRefused = true;
      notify();
    }
  });
}

function wire(sink: Sink): void {
  if (!ctx || !output) return;
  try {
    sink.source?.disconnect();
    sink.gain?.disconnect();
    sink.source = ctx.createMediaStreamSource(sink.stream);
    sink.analyser = ctx.createAnalyser();
    sink.analyser.fftSize = 512;
    sink.buf = new Float32Array(sink.analyser.fftSize);
    sink.gain = ctx.createGain();
    sink.source.connect(sink.analyser);
    sink.source.connect(sink.gain).connect(output);
  } catch {
    // A context that refuses the stream leaves this voice unheard, which the
    // speaking dot then shows as silence; nothing else is affected.
    sink.source = null;
    sink.analyser = null;
    sink.gain = null;
  }
}

/** Starts playing a player's pulled track, replacing any track already held for them. */
export function attachRemoteTrack(playerId: string, track: MediaStreamTrack): void {
  detachRemoteTrack(playerId);
  const stream = new MediaStream([track]);
  const el = new Audio();
  el.autoplay = true;
  el.muted = true;
  el.srcObject = stream;
  const sink: Sink = { stream, el, source: null, analyser: null, gain: null, buf: null };
  sinks.set(playerId, sink);
  // No context yet (an auto-join with no tap since the page loaded): make one
  // anyway. It may start suspended, which is what raises "Tap to hear".
  ensureVoiceContext();
  wire(sink);
  playElement(el);
  notify();
}

export function detachRemoteTrack(playerId: string): void {
  const sink = sinks.get(playerId);
  if (!sink) return;
  sinks.delete(playerId);
  try {
    sink.source?.disconnect();
    sink.gain?.disconnect();
  } catch {
    /* already gone with its context */
  }
  sink.el.pause();
  sink.el.srcObject = null;
}

/**
 * Silences one player at once, ahead of the close that will stop their track:
 * a mute has to take effect on the tap, not after a renegotiation queued behind
 * whatever else is in flight.
 */
export function silenceRemote(playerId: string): void {
  const sink = sinks.get(playerId);
  if (sink?.gain) sink.gain.gain.value = 0;
}

export function detachAllRemoteTracks(): void {
  for (const id of [...sinks.keys()]) detachRemoteTrack(id);
}

function rmsOf(analyser: AnalyserNode, buf: Float32Array<ArrayBuffer>): number {
  analyser.getFloatTimeDomainData(buf);
  let sum = 0;
  for (const v of buf) sum += v * v;
  return Math.sqrt(sum / buf.length);
}

/** RMS of the last few milliseconds of a player's voice, 0..1; 0 when not heard. */
export function remoteLevel(playerId: string): number {
  const sink = sinks.get(playerId);
  if (!sink?.analyser || !sink.buf) return 0;
  return rmsOf(sink.analyser, sink.buf);
}

// ---- the player's own mic ---------------------------------------------------
//
// So a player's own name lights when they talk, which is how they learn the mic
// works. The source feeds an analyser and NOTHING else: wiring it to the output
// would play the player back to themselves.

interface LocalMeter {
  track: MediaStreamTrack;
  source: MediaStreamAudioSourceNode;
  analyser: AnalyserNode;
  buf: Float32Array<ArrayBuffer>;
}

let localMeter: LocalMeter | null = null;
/** The context a meter was built in, so one rebuilt under it is noticed. */
let localMeterCtx: AudioContext | null = null;

/** RMS of the last few milliseconds of the given mic track, 0..1; 0 when it cannot be measured. */
export function localMicLevel(track: MediaStreamTrack): number {
  if (!ctx || ctx.state === "closed") return 0;
  if (localMeter?.track !== track || localMeterCtx !== ctx) {
    releaseLocalMicMeter();
    try {
      const source = ctx.createMediaStreamSource(new MediaStream([track]));
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      localMeter = { track, source, analyser, buf: new Float32Array(analyser.fftSize) };
      localMeterCtx = ctx;
    } catch {
      // A context that refuses the stream leaves the name unlit; nothing else is affected.
      return 0;
    }
  }
  return rmsOf(localMeter.analyser, localMeter.buf);
}

export function releaseLocalMicMeter(): void {
  try {
    localMeter?.source.disconnect();
  } catch {
    /* already gone with its context */
  }
  localMeter = null;
  localMeterCtx = null;
}

export function heardPlayerIds(): string[] {
  return [...sinks.keys()];
}

// ---- the mic, for the audiodbg=1 panel ------------------------------------
//
// On an iPhone a captured mic keeps iOS in call mode, whose processing made the
// game's music crackle (2026-10-09); that is why muting now unpublishes and
// releases the mic (voiceClient.ts, mute). The panel shows voice's context, the mic,
// and - 800 ms after the mic opens or is released - the rate a context made
// then gets, beside the two that exist. `audiofix=noaec` is the one experiment
// left, for the crackle while the mic is ON: see micConstraints in voiceClient.ts.

/** How long iOS is given to switch its audio session before anything is measured. */
const SESSION_SETTLE_MS = 800;

let micTrack: MediaStreamTrack | null = null;
let captureTimer: ReturnType<typeof setTimeout> | null = null;

audioDiagLine("voice", () => {
  const mic = micTrack ? `${micTrack.readyState}${micTrack.enabled ? " on" : " off"}` : "none";
  return `voice ctx ${ctx ? `${ctx.state}@${ctx.sampleRate}` : "none"}  heard ${sinks.size}  mic ${mic}${audioFixes.length ? `  fix:${audioFixes.join(",")}` : ""}`;
});

/** The rate a context created right now gets, which follows the current audio session. */
function probeRate(): string {
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return "?";
  try {
    const probe = new Ctor();
    const rate = probe.sampleRate;
    void probe.close().catch(() => {});
    return String(rate);
  } catch {
    return "?";
  }
}

/**
 * The voice client's mic: the captured track, or null once it is stopped. A
 * replacement track (a re-publish) is neither an open nor a release.
 */
export function noteMicCapture(track: MediaStreamTrack | null): void {
  const opened = !!track && !micTrack;
  const released = !track && !!micTrack;
  micTrack = track;
  if (!opened && !released) return;
  const what = opened ? "mic opened" : "mic released";
  audioLog(what);
  if (!audioDiagEnabled) return;
  if (captureTimer !== null) clearTimeout(captureTimer);
  captureTimer = setTimeout(() => {
    captureTimer = null;
    audioLog(`${what} +${SESSION_SETTLE_MS}ms: new ctx @${probeRate()} (game @${sound.sampleRate ?? "-"}, voice @${ctx?.sampleRate ?? "-"})`);
  }, SESSION_SETTLE_MS);
}

